import React, { useEffect, useState } from 'react';
import type { PluginInterface, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { SwiklyService, type CreateDepositPayload } from '../services/SwiklyService';
import AutoTranslateText from '../components/AutoTranslateText';
import { usePaymentApiKeys } from '../hooks/usePaymentApiKeys';
import { Editor } from '@tinymce/tinymce-react';
import { withTabLink } from '../utils/tinymceTabLink';
import { useTinyMceApiKey } from '../hooks/useTinyMceApiKey';

export interface SwiklyDepositConfig {
  id?: string; // ✅ AJOUT : ID unique pour l'instance
  ownerId?: string | number;
  accountId?: string;
  title: string;
  titleFontFamily?: string;
  titleFontSize?: number;
  titleBold?: boolean;
  titleItalic?: boolean;
  titleColor?: string;
  titleUnderline?: boolean;
  titleAlign?: 'left' | 'center' | 'right';
  amount: number;
  currency: 'EUR' | 'USD' | 'GBP';
  description?: string;
  descriptionFontFamily?: string;
  descriptionFontSize?: number;
  descriptionBold?: boolean;
  descriptionItalic?: boolean;
  descriptionColor?: string;
  descriptionUnderline?: boolean;
  descriptionAlign?: 'left' | 'center' | 'right';
  reference?: string;
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string; };
  redirectSuccessUrl?: string;
  redirectCancelUrl?: string;
  expirationDays?: number;
  captureMode?: 'manual' | 'auto';
  env?: 'sandbox' | 'production';
  serverBase?: string; // ex: /api/swikly
  depositId?: string;
  depositLink?: string;
}

const defaultConfig: SwiklyDepositConfig = {
  title: 'Caution Swikly',
  accountId: '',
  titleFontFamily: 'inherit',
  titleFontSize: 18,
  titleBold: true,
  titleItalic: false,
  titleColor: '#0f172a',
  titleUnderline: false,
  titleAlign: 'left',
  amount: 500,
  currency: 'EUR',
  description: '',
  descriptionFontFamily: 'inherit',
  descriptionFontSize: 14,
  descriptionBold: false,
  descriptionItalic: false,
  descriptionColor: '#475569',
  descriptionUnderline: false,
  descriptionAlign: 'left',
  reference: '',
  customer: { firstName: '', lastName: '', email: '', phone: '' },
  redirectSuccessUrl: '',
  redirectCancelUrl: '',
  expirationDays: 7,
  captureMode: 'manual',
  env: 'sandbox',
  serverBase: '/api/swikly',
  depositId: undefined,
  depositLink: undefined,
};

const ConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  const cfg = { ...defaultConfig, ...(config as SwiklyDepositConfig) };
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const { hasSwiklyKey, swiklyApiKey, isLoading, error } = usePaymentApiKeys();
  const { apiKey: tinymceApiKey, isLoading: tinymceLoading } = useTinyMceApiKey();
  const fontOptions = [
    { label: 'Par défaut', value: 'inherit' },
    { label: 'Inter', value: "'Inter', sans-serif" },
    { label: 'Roboto', value: "'Roboto', sans-serif" },
    { label: 'Open Sans', value: "'Open Sans', sans-serif" },
    { label: 'Lato', value: "'Lato', sans-serif" },
    { label: 'Montserrat', value: "'Montserrat', sans-serif" },
    { label: 'Poppins', value: "'Poppins', sans-serif" },
    { label: 'Playfair Display', value: "'Playfair Display', serif" },
    { label: 'Merriweather', value: "'Merriweather', serif" }
  ];

  const update = (patch: Partial<SwiklyDepositConfig>) =>
    onConfigChange({ ...cfg, ...patch });

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    if (cfg.ownerId) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      if (ownerId) {
        onConfigChange({ ...cfg, ownerId: String(ownerId) });
      }
    } catch {
      // ignore
    }
  }, [cfg, onConfigChange]);

  const ping = async () => {
    try {
      setBusy(true);
      await SwiklyService.ping(cfg.serverBase, swiklyApiKey || undefined);
      alert('Connexion backend Swikly OK');
    } catch (e: any) {
      alert(`Ping KO: ${e?.message || 'Erreur'}`);
    } finally {
      setBusy(false);
    }
  };

  const generateLink = async () => {
    try {
      setBusy(true);
      const payload: CreateDepositPayload = {
        accountId: cfg.accountId || undefined,
        amount: Number(cfg.amount),
        currency: cfg.currency,
        description: cfg.description || undefined,
        reference: cfg.reference || undefined,
        customer: cfg.customer,
        redirectSuccessUrl: cfg.redirectSuccessUrl || undefined,
        redirectCancelUrl: cfg.redirectCancelUrl || undefined,
        expirationDays: cfg.expirationDays || undefined,
        captureMode: cfg.captureMode || 'manual',
        env: cfg.env || 'sandbox',
      };
      const res = await SwiklyService.createDeposit(payload, cfg.serverBase, swiklyApiKey || undefined);
      update({ depositId: res.depositId, depositLink: res.url });
    } catch (e: any) {
      alert(`Génération échouée: ${e?.message || 'Erreur'}`);
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = async () => {
    if (!cfg.depositId) return;
    try {
      setBusy(true);
      const s = await SwiklyService.getDepositStatus(cfg.depositId, cfg.serverBase, swiklyApiKey || undefined, cfg.accountId);
      setStatus(`${s.status} • ${s.amount} ${s.currency}`);
    } catch (e: any) {
      alert(`Statut indisponible: ${e?.message || 'Erreur'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="d-flex align-items-center gap-2 mb-3">
        <h4 className="mb-0">⚡ Caution Swikly</h4>
      </div>

      {isLoading && <div className="alert alert-info small p-2">Chargement des clés…</div>}
      {error && <div className="alert alert-danger small p-2">Erreur de récupération des clés.</div>}
      {!isLoading && !error && (
        hasSwiklyKey
          ? <div className="alert alert-success small p-2">✅ Clé API Swikly détectée.</div>
          : <div className="alert alert-warning small p-2">Aucune clé API Swikly trouvée (service=Swikly, provider=swikly).</div>
      )}

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Titre</label>
          <input className="form-control" value={cfg.title} onChange={(e) => update({ title: e.target.value })} />
        </div>
        <div className="col-md-3">
          <label className="form-label">Montant</label>
          <input className="form-control" type="number" min={1} value={cfg.amount} onChange={(e) => update({ amount: Number(e.target.value) || 0 })} />
        </div>
        <div className="col-md-3">
          <label className="form-label">Devise</label>
          <select className="form-select" value={cfg.currency} onChange={(e) => update({ currency: e.target.value as any })}>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>

        <div className="col-12">
          <div className="bg-light border rounded px-3 py-2 text-muted fw-semibold">Style du titre</div>
        </div>
        <div className="col-md-4">
          <label className="form-label">Police</label>
          <select className="form-select" value={cfg.titleFontFamily || 'inherit'} onChange={(e) => update({ titleFontFamily: e.target.value })}>
            {fontOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">Taille</label>
          <input className="form-control" type="number" min={10} value={cfg.titleFontSize || 18} onChange={(e) => update({ titleFontSize: Number(e.target.value) || 18 })} />
        </div>
        <div className="col-md-3 d-flex flex-column">
          <label className="form-label">Style</label>
          <div className="btn-group" role="group" aria-label="Style titre">
            <button type="button" className={`btn btn-outline-secondary ${cfg.titleBold ? 'active' : ''}`} onClick={() => update({ titleBold: !cfg.titleBold })}><strong>B</strong></button>
            <button type="button" className={`btn btn-outline-secondary ${cfg.titleItalic ? 'active' : ''}`} onClick={() => update({ titleItalic: !cfg.titleItalic })}><em>I</em></button>
            <button type="button" className={`btn btn-outline-secondary ${cfg.titleUnderline ? 'active' : ''}`} onClick={() => update({ titleUnderline: !cfg.titleUnderline })}><span style={{ textDecoration: 'underline' }}>U</span></button>
          </div>
        </div>
        <div className="col-md-2">
          <label className="form-label">Alignement</label>
          <select className="form-select" value={cfg.titleAlign || 'left'} onChange={(e) => update({ titleAlign: e.target.value as any })}>
            <option value="left">Gauche</option>
            <option value="center">Centre</option>
            <option value="right">Droite</option>
          </select>
        </div>
        <div className="col-md-1">
          <label className="form-label">Couleur</label>
          <input className="form-control form-control-color" type="color" value={cfg.titleColor || '#0f172a'} onChange={(e) => update({ titleColor: e.target.value })} />
        </div>

        <div className="col-md-6">
          <label className="form-label">Référence</label>
          <input className="form-control" value={cfg.reference || ''} onChange={(e) => update({ reference: e.target.value })} placeholder="ex: booking-1234" />
        </div>
        <div className="col-md-6">
          <label className="form-label">Account ID Swikly</label>
          <input className="form-control" value={cfg.accountId || ''} onChange={(e) => update({ accountId: e.target.value })} placeholder="ex: E67F... (accountId)" />
        </div>
        <div className="col-md-6">
          <label className="form-label">Endpoint backend</label>
          <input className="form-control" value={cfg.serverBase || '/api/swikly'} onChange={(e) => update({ serverBase: e.target.value })} />
        </div>

        <div className="col-12">
          <div className="bg-light border rounded px-3 py-2 text-muted fw-semibold">Description</div>
        </div>
        <div className="col-12">
          <label className="form-label">Description</label>
          {tinymceLoading ? (
            <div className="alert alert-info small p-2">Chargement de l’éditeur…</div>
          ) : !tinymceApiKey ? (
            <div className="alert alert-warning small p-2">Clé TinyMCE manquante</div>
          ) : (
            <Editor
              apiKey={tinymceApiKey}
              value={cfg.description || ''}
              onEditorChange={(value) => update({ description: value })}
              init={withTabLink({
                height: 180,
                menubar: false,
                plugins: 'lists link',
                toolbar: 'bold italic underline | bullist numlist | link | removeformat',
                content_style: `
                  body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                  img[style*="float: left"] { margin: 0 12px 12px 0; }
                  img[style*="float: right"] { margin: 0 0 12px 12px; }
                `
              })}
            />
          )}
        </div>

        <div className="col-md-4">
          <label className="form-label">Police</label>
          <select className="form-select" value={cfg.descriptionFontFamily || 'inherit'} onChange={(e) => update({ descriptionFontFamily: e.target.value })}>
            {fontOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">Taille</label>
          <input className="form-control" type="number" min={10} value={cfg.descriptionFontSize || 14} onChange={(e) => update({ descriptionFontSize: Number(e.target.value) || 14 })} />
        </div>
        <div className="col-md-3 d-flex flex-column">
          <label className="form-label">Style</label>
          <div className="btn-group" role="group" aria-label="Style description">
            <button type="button" className={`btn btn-outline-secondary ${cfg.descriptionBold ? 'active' : ''}`} onClick={() => update({ descriptionBold: !cfg.descriptionBold })}><strong>B</strong></button>
            <button type="button" className={`btn btn-outline-secondary ${cfg.descriptionItalic ? 'active' : ''}`} onClick={() => update({ descriptionItalic: !cfg.descriptionItalic })}><em>I</em></button>
            <button type="button" className={`btn btn-outline-secondary ${cfg.descriptionUnderline ? 'active' : ''}`} onClick={() => update({ descriptionUnderline: !cfg.descriptionUnderline })}><span style={{ textDecoration: 'underline' }}>U</span></button>
          </div>
        </div>
        <div className="col-md-2">
          <label className="form-label">Alignement</label>
          <select className="form-select" value={cfg.descriptionAlign || 'left'} onChange={(e) => update({ descriptionAlign: e.target.value as any })}>
            <option value="left">Gauche</option>
            <option value="center">Centre</option>
            <option value="right">Droite</option>
          </select>
        </div>
        <div className="col-md-1">
          <label className="form-label">Couleur</label>
          <input className="form-control form-control-color" type="color" value={cfg.descriptionColor || '#475569'} onChange={(e) => update({ descriptionColor: e.target.value })} />
        </div>
      </div>

      <fieldset className="border rounded p-3 mt-3">
        <legend className="float-none w-auto px-2">Client</legend>
        <div className="row g-3">
          <div className="col-md-6">
            <input className="form-control" placeholder="Prénom" value={cfg.customer?.firstName || ''} onChange={(e) => update({ customer: { ...cfg.customer, firstName: e.target.value } })} />
          </div>
          <div className="col-md-6">
            <input className="form-control" placeholder="Nom" value={cfg.customer?.lastName || ''} onChange={(e) => update({ customer: { ...cfg.customer, lastName: e.target.value } })} />
          </div>
          <div className="col-md-6">
            <input className="form-control" placeholder="Email" value={cfg.customer?.email || ''} onChange={(e) => update({ customer: { ...cfg.customer, email: e.target.value } })} />
          </div>
          <div className="col-md-6">
            <input className="form-control" placeholder="Téléphone" value={cfg.customer?.phone || ''} onChange={(e) => update({ customer: { ...cfg.customer, phone: e.target.value } })} />
          </div>
        </div>
      </fieldset>

      <div className="row g-3 mt-3">
        <div className="col-md-6">
          <label className="form-label">Succès URL</label>
          <input className="form-control" value={cfg.redirectSuccessUrl || ''} onChange={(e) => update({ redirectSuccessUrl: e.target.value })} placeholder="https://..." />
        </div>
        <div className="col-md-6">
          <label className="form-label">Annulation URL</label>
          <input className="form-control" value={cfg.redirectCancelUrl || ''} onChange={(e) => update({ redirectCancelUrl: e.target.value })} placeholder="https://..." />
        </div>
        <div className="col-md-4">
          <label className="form-label">Expiration (jours)</label>
          <input className="form-control" type="number" min={1} value={cfg.expirationDays || 7} onChange={(e) => update({ expirationDays: Number(e.target.value) || undefined })} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Capture</label>
          <select className="form-select" value={cfg.captureMode || 'manual'} onChange={(e) => update({ captureMode: e.target.value as any })}>
            <option value="manual">Manuelle</option>
            <option value="auto">Automatique</option>
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">Environnement</label>
          <select className="form-select" value={cfg.env || 'sandbox'} onChange={(e) => update({ env: e.target.value as any })}>
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </div>
      </div>

      <div className="d-flex gap-2 flex-wrap mt-3">
        <button className="btn btn-outline-secondary" onClick={ping} disabled={busy || isLoading}>🧪 Tester connexion</button>
        <button className="btn btn-primary" onClick={generateLink} disabled={busy || isLoading || !cfg.amount || !hasSwiklyKey}>🔗 Générer le lien</button>
        {cfg.depositId && <button className="btn btn-outline-primary" onClick={refreshStatus} disabled={busy || isLoading}>🔄 Rafraîchir statut</button>}
      </div>

      {cfg.depositLink && (
        <div className="bg-light border rounded p-3 mt-3">
          <div><strong>Lien:</strong> <a href={cfg.depositLink} target="_blank" rel="noreferrer">{cfg.depositLink}</a></div>
          <div><strong>ID:</strong> {cfg.depositId}</div>
          {status && <div><strong>Statut:</strong> {status}</div>}
          <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => navigator.clipboard.writeText(cfg.depositLink!)}>📋 Copier</button>
        </div>
      )}
    </div>
  );
};

const PreviewComponent: React.FC<PluginPreviewProps> = ({ config }) => {
  const cfg = { ...defaultConfig, ...(config as SwiklyDepositConfig) };
  const hasLink = !!cfg.depositLink;
  const titleStyle: React.CSSProperties = {
    fontFamily: cfg.titleFontFamily || 'inherit',
    fontSize: cfg.titleFontSize || 18,
    fontWeight: cfg.titleBold ? 700 : 400,
    fontStyle: cfg.titleItalic ? 'italic' : 'normal',
    color: cfg.titleColor || '#0f172a',
    textDecoration: cfg.titleUnderline ? 'underline' : 'none',
    textAlign: cfg.titleAlign || 'left'
  };
  const descriptionStyle: React.CSSProperties = {
    fontFamily: cfg.descriptionFontFamily || 'inherit',
    fontSize: cfg.descriptionFontSize || 14,
    fontWeight: cfg.descriptionBold ? 700 : 400,
    fontStyle: cfg.descriptionItalic ? 'italic' : 'normal',
    color: cfg.descriptionColor || '#475569',
    textDecoration: cfg.descriptionUnderline ? 'underline' : 'none',
    textAlign: cfg.descriptionAlign || 'left'
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: 'linear-gradient(180deg,#fff,#f9fafb)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 22 }}>🛡️</span>
        <strong style={titleStyle}>{cfg.title || 'Caution Swikly'}</strong>
      </div>
      {cfg.description && (
        <div
          style={{ marginBottom: 8, ...descriptionStyle }}
          dangerouslySetInnerHTML={{ __html: cfg.description }}
        />
      )}
      <div style={{ color: '#475569', marginBottom: 12 }}>
        <AutoTranslateText sourceLang="fr">
          {`Montant de la caution: ${cfg.amount} ${cfg.currency}`}
        </AutoTranslateText>
      </div>
      <button
        disabled={!hasLink}
        onClick={() => { if (cfg.depositLink) window.open(cfg.depositLink, '_blank'); }}
        style={{
          background: hasLink ? '#0ea5e9' : '#94a3b8',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          padding: '10px 14px',
          cursor: hasLink ? 'pointer' : 'not-allowed',
          fontWeight: 600
        }}
        title={hasLink ? 'Ouvrir la page de caution' : 'Lien non généré'}
      >
        {hasLink ? 'Payer la caution' : 'Lien non généré'}
      </button>
      {!hasLink && (
        <div style={{ marginTop: 8, color: '#64748b', fontSize: 12 }}>
          Générez le lien dans la configuration du plugin.
        </div>
      )}
    </div>
  );
};

const SwiklyDepositPluginModular: PluginInterface = {
  metadata: {
    id: 'swikly-deposit',
    name: 'Caution Swikly',
    version: '1.0.0',
    description: 'Demande de dépôt/caution via Swikly (génération de lien, statut)',
    author: 'Book Editor Team',
    icon: '🛡️',
    category: 'commerce',
    tags: ['payment', 'deposit', 'caution', 'swikly']
  },
  defaultConfig,
  ConfigComponent,
  PreviewComponent,
  
  // ✅ CORRECTION MAJEURE : GÉNÉRATION D'ID UNIQUE
  generateHTML: (config: SwiklyDepositConfig) => {
    // 1. Génération de l'ID unique
    const instanceId = config.id || `swikly-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // 2. Persistance de l'ID
    const configWithId = { ...config, id: instanceId };

    return generateUniformPlaceholder({
      pluginId: instanceId, // ✅ Utilisation de l'ID unique
      pluginType: 'swikly-deposit',
      category: 'commerce',
      title: config.title || 'Caution Swikly',
      description: `Caution ${config.amount} ${config.currency}`,
      icon: '🛡️',
      config: configWithId // ✅ On passe la config enrichie
    });
  },

  // ✅ DOCUMENTATION POUR L'IA (Déjà présente, conservée)
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Bloc de caution Swikly avec génération de lien, suivi de statut et styles personnalisables.",
    usage: "Utilise ce plugin pour les cautions/dépôts de garantie via Swikly. Nécessite une clé API Swikly côté profil.",
    configSchema: {
      id: "Identifiant unique",
      ownerId: "ID du créateur (récupération des clés Swikly)",
      accountId: "Account ID Swikly (optionnel selon compte)",
      title: "Titre affiché",
      titleFontFamily: "Police du titre",
      titleFontSize: "Taille du titre (px)",
      titleBold: "Titre en gras",
      titleItalic: "Titre en italique",
      titleUnderline: "Titre souligné",
      titleColor: "Couleur du titre",
      titleAlign: "'left' | 'center' | 'right'",
      amount: "Montant de la caution",
      currency: "'EUR' | 'USD' | 'GBP'",
      description: "Description HTML (TinyMCE)",
      descriptionFontFamily: "Police de description",
      descriptionFontSize: "Taille de description (px)",
      descriptionBold: "Description en gras",
      descriptionItalic: "Description en italique",
      descriptionUnderline: "Description soulignée",
      descriptionColor: "Couleur de description",
      descriptionAlign: "'left' | 'center' | 'right'",
      reference: "Référence interne (ex: booking-1234)",
      customer: "{ firstName, lastName, email, phone }",
      redirectSuccessUrl: "URL de succès",
      redirectCancelUrl: "URL d'annulation",
      expirationDays: "Nombre de jours avant expiration",
      captureMode: "'manual' | 'auto'",
      env: "'sandbox' | 'production'",
      serverBase: "Endpoint backend Swikly (ex: /api/swikly)",
      depositId: "ID du dépôt (généré)",
      depositLink: "Lien Swikly (généré)"
    },
    exampleConfig: {
      title: "Caution Hébergement",
      amount: 500,
      currency: "EUR",
      accountId: "ACC_XXXX",
      reference: "booking-1234",
      customer: { firstName: "Jean", lastName: "Dupont", email: "jean@example.com" },
      env: "sandbox",
      captureMode: "manual",
      serverBase: "/api/swikly"
    }
  }
};

export default SwiklyDepositPluginModular;
